import { buffer, lineString } from 'turf'
import L from 'leaflet'
import { throttle } from 'lodash-unified'
import { CommUtils } from '@map-sdk/sdk/utils/CommUtils'
import { CanvasShipUtils } from '@map-sdk/sdk/utils/CanvasShipUtils'
import { AreaViewBase } from '@map-sdk/sdk/plugins/AreaView/AreaViewBase'
import { data } from '@map-sdk/sdk/plugins/AreaView/layer/SeaRouteSymbolData'
import type { AreaViewBaseOptions } from '@map-sdk/sdk/plugins/AreaView/AreaViewBase'
import type { MyMap } from '@map-sdk/sdk/ShipxyAPISDK'

class SeaRouteSymbol extends AreaViewBase {
  _tempAreaArray: L.Layer[] = []
  lyGroup: L.LayerGroup | null = null
  isShow = false
  hash: { [name: string]: any } = {}
  hashSD: { [name: string]: any } = {}
  layerHash: { [name: string]: any } = {}
  layerHashName: { [name: string]: any } = {}
  textLayers: any[] = []
  textZoom: number[] = [8, 18]
  isInit = true
  _zoomend = false
  data2: { [key: string]: any } = data.data2
  data: {
    point: number
    lat: number
    lng: number
    name: string
    split: number[]
    distance: number
    isSpec?: boolean
  }[] = data.data
  pendingUpdates = false

  constructor(map: MyMap, options?: Partial<AreaViewBaseOptions>) {
    super(map, { minzoom: 2, maxzoom: 18, ...options })
    this._data = this.data2
    this.data.forEach((item) => {
      const point: [number, number] = [item.lat, item.lng]
      if (this.hash[item.name]) {
        this.hash[item.name].points.push(point)
      } else {
        this.hash[item.name] = {
          split: item.split || [],
          name: item.name,
          distance: item.distance / 2,
          points: [point],
          isSpec: !!item.isSpec,
        }
      }
    })

    Object.values(this.hash).forEach((o) => {
      const collection: GeoJSON.Feature<GeoJSON.LineString> = lineString(
        o.points.map((p: any[]) => [p[1], p[0]])
      )
      const feature: GeoJSON.Feature<GeoJSON.Polygon> = buffer(
        collection as any,
        o.distance,
        'nauticalmiles'
      )
      const arr: number[][] = []
      let split: number[] = o.split
      feature.geometry.coordinates[0].forEach((item: any, i: number) => {
        split = o.split
        if (!split.includes(i)) {
          let temp = item
          if (o.isSpec) {
            if (i === 0 || i === 6) {
              temp = [120.26754740043194, 36.04663280221668]
            }
            if (i === 2) {
              temp = [120.26078763753424, 36.02225510441438]
            }
          }
          arr.push(temp)
        }
      })
      feature.geometry.coordinates[0] = arr
      o.feature = feature
    })

    map.on(
      'moveend',
      throttle(() => {
        if (this.lyGroup && !this.pendingUpdates) {
          this.pendingUpdates = true
          this.lyGroup.eachLayer((ly: any) => {
            if (ly && ly._type === 'line') {
              this.setText(ly)
            }
          })
          this.pendingUpdates = false
        }
      }, 1000)
    )

    this.polyNameChange()
    this.isInit = true
  }

  _show() {
    Object.values(this.hash).forEach((item) => {
      this.showRoute(item)
    })
    this.showNormalPoly()
    this.isShow = true
    this.bounds && this.isInit && this._map.fitBounds(this.bounds as any)
    this.isInit = false
  }
  _hide() {
    try {
      this.isShow = false
      this.lyGroup && this.lyGroup.clearLayers()
      const layerHashNames = Object.keys(this.layerHashName).filter(
        (item) => !!this.layerHashName[item]
      )
      for (const item of layerHashNames) {
        if (this._map.hasLayer(this.layerHashName[item]))
          this._map.removeLayer(this.layerHashName[item])
        delete this.layerHashName[item]
      }
      for (const item in this.layerHash) {
        this._map.removeLayer(this.layerHash[item])
        delete this.layerHash[item]
      }
      this._map.off('zoomend', this._EventZoomEnd)
    } catch (e) {
      console.log(e)
    }
  }
  showName(p: any, name = '') {
    const color = this._map.mapType != 'MT_SATELLITE' ? '#2161c0' : '#fff'
    const zoom = this._map.getZoom()
    if (p.getLatLngs()[0].length == 2) {
      if (p._textNode) {
        p._text = ''
        p._textNode.remove()
        p._textNode = null
      }
      if (zoom > this.textZoom[0] && zoom < this.textZoom[1]) {
        p.redraw()
        p.setText(name, {
          repeat: false,
          offset: -7,
          center: false, //居中
          attributes: {
            fill: color,
            //'font-weight': '600',
            'font-size': '11',
            //'font-family': "微软雅黑",
            'letter-spacing': '4px',
          },
        })
      }
      return
    }

    return CommUtils.getFeatureLabel(p, name, {
      offset: 0,
      attributes: {
        fill: color,
        fontSize: '11',
        xShift: 0,
        fontFamily: '微软雅黑',
      },
    })
  }
  //调整区域名称偏移
  getDescriptOffset() {
    const offset = {
      X: 10,
      Y: 0,
    }
    return offset
  }
  private _EventZoomEnd = () => {
    this._zoomend = true
  }
  ///
  //  区域名称绘制逻辑
  //  1:当前可视范围内区域名称重绘
  //  2:处于编辑的区域屏蔽展示
  //  3:事件调整 缩放执行延迟添加
  ///
  polyNameChange() {
    this._map.on('zoomend', this._EventZoomEnd)

    this._map.on('moveend', () => {
      this._tempAreaArray = []
      for (const item in this.layerHashName) {
        const ly = this.layerHashName[item]
        ly && this._map.removeLayer(ly)

        if (this.layerHash[item]) {
          const lbounds = this.layerHash[item].getBounds()
          if (lbounds.isValid() && this._map.getBounds().intersects(lbounds)) {
            this.layerHashName[item] = this.showName(this.layerHash[item], item)
            // 缩放延迟执行添加
            if (!this._zoomend) {
              this.layerHashName[item] &&
                this._map.addLayer(this.layerHashName[item])
            } else {
              this._tempAreaArray.push(this.layerHashName[item])
            }
          }
        }
      }

      // 延迟绘制
      setTimeout(() => {
        this._tempAreaArray.forEach((item: L.Layer) => {
          item && this._map.addLayer(item)
        })
      }, 0)
      this._zoomend = false
    })
  }
  showNormalPoly() {
    Object.keys(this.data2).forEach((key) => {
      //处理只有两个点的情况
      const latLngs = this.data2[key].map((c: L.LatLngExpression) => {
        return CanvasShipUtils.GPSEncryptByMapToLatLng(c, this._map)
      })
      const poly = L.polygon(latLngs, {
        fillOpacity: 0.2,
        opacity: 0.4,
        weight: 1,
        fillColor: '#b65aa3',
        color: '#b65aa3',
      }) as any
      poly._type = 'poly2'
      this.layerHash[key] = poly
      this._map.addLayer(poly)
      // 只有在合适的缩放级别才添加文本标注
      this.layerHashName[key] = this.showName(poly, key)
      this.layerHashName[key] && this._map.addLayer(this.layerHashName[key])
    })
  }
  showRoute(o: any) {
    if (!this.lyGroup) this.lyGroup = L.layerGroup().addTo(this._map)
    this.bounds = this.bounds.concat(o.points)
    //绘制线段 多段绘制
    o.points.forEach((p: number[], i: number) => {
      const next = o.points[i + 1]
      if (next) {
        let p1 = L.latLng(p[0], p[1])
        let next1 = L.latLng(next[0], next[1])
        p1 = CanvasShipUtils.GPSEncryptByMapToLatLng(p1, this._map)
        next1 = CanvasShipUtils.GPSEncryptByMapToLatLng(next1, this._map)
        const lls = [p1, next1]
        const angle = CanvasShipUtils.getAngleByLatLngToBigCircle(
          {
            lat: p1.lat,
            lng: p1.lng,
          },
          {
            lat: next1.lat,
            lng: next1.lng,
          }
        )
        if (angle > 180) lls.reverse()
        const polyLine = L.polyline(lls, {
          dashArray: [6, 6],
          weight: 2,
          opacity: 0.9,
          color: this.setLineColor(),
        }) as any
        polyLine._type = 'line'
        polyLine.idx = i
        polyLine._name = o.name
        polyLine._mapType = this._map.getMapType()
        polyLine._center = true

        //只绘制偶数段
        //if (i % 2 == 0) setText(polyLine);
        this.lyGroup && this.lyGroup.addLayer(polyLine)
      }
    })
    //

    const feature = o.feature
    feature.geometry.coordinates = o.feature.geometry.coordinates.map(
      (coordinate: any[]) => {
        return coordinate.map((lngLat) => {
          const latLng = CanvasShipUtils.GPSEncryptByMapToLatLng(
            [lngLat[1], lngLat[0]],
            this._map
          )
          return [latLng.lng, latLng.lat]
        })
      }
    )
    const poly = L.geoJSON(feature, {
      fillOpacity: 0.2,
      opacity: 0.4,
      weight: 1,
      fillColor: '#b65aa3',
      color: '#b65aa3',
    } as any) as any
    poly._type = 'poly'
    this.lyGroup.addLayer(poly)
  }
  setLineColor() {
    if (this._map.getMapType() == 'MT_SATELLITE') return '#92b2cb'
    return '#628cd9'
  }
  setText(polyLine: any) {
    const setTextColor = () => {
      if (this._map.getMapType() == 'MT_SATELLITE') return '#fff'
      return '#2161c0'
    }
    const textColor = setTextColor()
    const lineColor = this.setLineColor()

    if (polyLine.options.color != lineColor) {
      polyLine && polyLine.setStyle({ color: lineColor })
      if (polyLine.idx % 4 == 0) {
        polyLine._textOptions &&
          polyLine._textOptions.attributes &&
          (polyLine._textOptions.attributes.fill = textColor)
        polyLine._textRedraw()
      }
      return
    }
    const zoom = this._map.getZoom()

    if (zoom > this.textZoom[0] && zoom < this.textZoom[1]) {
      if (polyLine.idx % 4 == 0 && polyLine._text != polyLine._name) {
        polyLine.setText(polyLine._name, {
          repeat: false,
          offset: -7,
          center: polyLine._center, //居中
          attributes: {
            fill: textColor,
            //'font-weight': '600',
            'font-size': '11',
            //'font-family': "微软雅黑",
            'letter-spacing': '4px',
          },
        })
      }
    } else {
      if (polyLine._textNode) {
        polyLine._text = ''
        polyLine._textNode.remove()
        polyLine._textNode = null
      }
    }
  }
}

const seaRouteSymbol = (map: MyMap, options?: Partial<AreaViewBaseOptions>) =>
  new SeaRouteSymbol(map, options)

export { SeaRouteSymbol, seaRouteSymbol }
